import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '@afosto/app-components-react/Form';
import EditItemForm from '../EditItemForm';
import InvoiceItem from '../InvoiceItem';
import ContentDrawer from '@afosto/app-components-react/ContentDrawer';
import StackItemList from '@afosto/app-components-react/StackItemList';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import formatAdjustments from '../../utils/formatAdjustments';
import uuid from '@afosto/utils/uuid';
import translations from './translations';

const ItemsFormSection = ({
  allowCreate,
  allowRemove,
  allowChangeQuantity,
  projection,
  isFetchingProjection,
  isPurchase,
}) => {
  const intl = useIntl();
  const [highlightedItem, setHighlightedItem] = useState(null);
  const formContext = useFormContext();
  const { setValue, watch } = formContext || {};
  const itemsValue = watch('items');

  /**
   * Handle creating a new item.
   * @param {string} sku
   * @param {string} description
   * @param {number} quantity
   * @param {product} product
   * @param {boolean} addAnotherProduct
   * @returns {Promise}
   */
  const handleCreateItem = async ({ sku, description, quantity, product }) => {
    const generatedID = uuid();
    const formattedQuantity = parseInt(quantity, 10);
    const item = {
      id: generatedID,
      reference: generatedID,
      sku,
      description,
      quantity: isPurchase ? Math.abs(formattedQuantity) * -1 : formattedQuantity,
      image: product?.image,
    };
    setValue('items', [...itemsValue, item]);
  };

  /**
   * Handle removing items.
   * @param {array} itemIds
   */
  const handleRemoveItems = itemIds => {
    const newItems = itemsValue.filter(({ id }) => !itemIds.includes(id));
    setValue('items', newItems);
  };

  /**
   * Handle editing an existing item.
   * @returns {Promise}
   */
  const handleEditItem = async ({ reference, amount, quantity, adjustments }) => {
    try {
      const otherItems = [...itemsValue].filter(item => item.reference !== reference);
      const currentItem = [...itemsValue].find(item => item.reference === reference);

      if (currentItem) {
        const formattedQuantity = parseInt(quantity, 10);
        const formattedAdjustments = adjustments
          .filter(adjustment => adjustment.amount !== '' && adjustment.description !== '')
          .reduce((acc, adjustment) => {
            const isPercentage = adjustment.type === 'percentage';

            return [
              ...acc,
              {
                reference: adjustment.id,
                amount: !isPercentage ? parseInt(adjustment.amount, 10) : adjustment.amount,
                is_percentage: isPercentage,
                is_discount: adjustment.isDiscount,
                description: adjustment.description,
                result: adjustment.result,
              },
            ];
          }, []);

        setValue('items', [
          ...otherItems,
          {
            ...currentItem,
            amount: parseInt(amount, 10),
            quantity: isPurchase ? Math.abs(formattedQuantity) * -1 : formattedQuantity,
            adjustments: formattedAdjustments,
          },
        ]);
        setHighlightedItem(null);
      }
    } catch (err) {
      // Do something with error.
    }
  };

  const formattedItems = itemsValue.reduce((acc, item) => {
    const projectionItem = findObjectInArray(projection?.items ?? [], 'reference', item.reference);

    return [
      ...acc,
      {
        ...item,
        amount: projectionItem?.amount ?? 1,
        currency: projection?.currency ?? 'EUR',
        subtotal: projectionItem?.subtotal ?? 0,
        total: projectionItem?.total ?? 0,
        adjustments: formatAdjustments(projectionItem?.adjustments),
        vat_percentage: projectionItem?.vat_percentage,
        total_revenue: projectionItem?.total_revenue,
      },
    ];
  }, []);

  const renderItem = (item, { SelectionComponent }) => (
    <InvoiceItem
      allowNegativeQuantity={!isPurchase}
      item={item}
      onClick={clickedItem => setHighlightedItem(clickedItem)}
      SelectionComponent={SelectionComponent}
      isVatShifted={projection.is_vat_shifted}
      isIncludingVat={projection.is_including_vat}
    />
  );

  const formattedHighlightedItem = highlightedItem
    ? {
        ...highlightedItem,
        quantity: isPurchase ? Math.abs(highlightedItem.quantity) : highlightedItem.quantity,
      }
    : null;

  return (
    <>
      <StackItemList
        items={formattedItems}
        renderItem={renderItem}
        onCreate={handleCreateItem}
        onRemove={handleRemoveItems}
        isLoading={isFetchingProjection}
        allowNegativeQuantity={!isPurchase}
        allowCreate={allowCreate}
        allowRemove={allowRemove}
      />

      <ContentDrawer
        title={intl.formatMessage(translations.editItem.title)}
        isOpen={highlightedItem !== null}
        handleClose={() => setHighlightedItem(null)}
        drawerWidth="800px"
      >
        <EditItemForm
          allowChangeQuantity={allowChangeQuantity}
          allowNegativeQuantity={!isPurchase}
          onCancel={() => setHighlightedItem(null)}
          onSubmit={handleEditItem}
          item={formattedHighlightedItem}
        />
      </ContentDrawer>
    </>
  );
};

ItemsFormSection.propTypes = {
  allowCreate: PropTypes.bool,
  allowRemove: PropTypes.bool,
  allowChangeQuantity: PropTypes.bool,
  projection: PropTypes.object,
  isFetchingProjection: PropTypes.bool,
  isPurchase: PropTypes.bool,
};

ItemsFormSection.defaultProps = {
  allowCreate: true,
  allowRemove: true,
  allowChangeQuantity: true,
  projection: null,
  isFetchingProjection: true,
  isPurchase: false,
};

export default ItemsFormSection;
